import { AxiosProgressEvent } from 'axios';
import {
  FILE_UPLOAD,
  FILE_PROCESS,
  FILE_PROCESS_STATUS,
  FILE_DELETE,
  GET_USER_SESSION_URL,
  FILE_REPOSITORY,
  FILE_UPDATE,
  FILE_DOWNLOAD,
} from '../constants/apiRoutes';
import { ApiError } from '../types/Content';
import {
  FileUploadIntiateResult,
  FileUploadIntiateResponse,
  FileProcessIntiateResult,
  FileProcessIntiateResponse,
  FileProcessStatusCheckResponse,
  FileProcessStatusCheckResult,
  FileDeleteResult,
  FileDeleteResponse,
  GetSessionReponse,
  GetSessionResult,
  UploadFileResponse,
  FileRepositoryResponse,
  FileRepositoryResult,
  FileUpdatePayload,
  FileUpdateResponse,
  FileUpdateResult,
  FileDownloadResponse,
  FileDownloadResult,
} from '../types/FileUpload';
import { httpClient, HTTPClientRequestPayload } from '../utils/httpClient';
import Logger from '../utils/logger';

/**
 * Initiates an API call to upload a file or multiple files.
 *
 * This function sends a POST request to the server to initiate the file upload process.
 * It handles the response and errors appropriately, logging any errors encountered during the process.
 *
 * @param {string} sessionId - The session ID associated with the file upload.
 * @param {string} [fileName] - Optional single file name for backward compatibility.
 * @param {string[]} [fileNames] - Optional array of file names for multiple file uploads.
 * @returns {Promise<FileUploadIntiateResult>} - A promise that resolves to the result of the file upload initiation.
 */
export const intiateFileUploadApiCall = async (
  sessionId?: string,
  fileName?: string,
  fileNames?: string[]
): Promise<FileUploadIntiateResult> => {
  const payload: HTTPClientRequestPayload = {
    ...(sessionId && { sessionId }),
    ...(fileName && { fileName }),
    ...(fileNames && { fileNames }),
  };
  try {
    const response = await httpClient.post<FileUploadIntiateResponse>(
      FILE_UPLOAD,
      payload
    );
    const { data, success } = response as FileUploadIntiateResponse;
    if (success && data) {
      return { success, data };
    }
    return {
      success: false,
    };
  } catch (error) {
    const postError = error as ApiError;
    Logger.error(new Error(postError.message));
    return {
      success: false,
    };
  }
};

/**
 * Initiates an API call to process a file after upload.
 *
 * This function sends a POST request to the server to initiate the file processing process.
 * It handles the response and errors appropriately, logging any errors encountered during the process.
 *
 * @param {string} sessionId - The session ID associated with the file upload.
 * @param {string} fileId - The unique identifier of the file to be processed.
 * @param {string} fileName - The name of the file that has been uploaded.
 * @param {string} fileUploadStatus - The status of the file upload, used to determine if processing can proceed.
 * @returns {Promise<FileProcessIntiateResult>} - A promise that resolves to the result of the file processing initiation.
 */

export const intiateFileProcessApiCall = async (
  sessionId: string,
  fileId: string,
  fileName: string,
  fileUploadStatus: string
): Promise<FileProcessIntiateResult> => {
  const payload: HTTPClientRequestPayload = {
    sessionId,
    fileId,
    fileName,
    fileUploadStatus,
  };
  try {
    const response = await httpClient.post<FileProcessIntiateResponse>(
      FILE_PROCESS,
      payload
    );
    const { data, success } = response as FileProcessIntiateResponse;
    if (success && data) {
      return { success, data };
    }
    return {
      success: false,
    };
  } catch (error) {
    const postError = error as ApiError;
    Logger.error(new Error(postError.message));
    return {
      success: false,
    };
  }
};

export const fileProcessStatusCheckApiCall = async (
  sessionId: string,
  fileIds?: string[]
): Promise<FileProcessStatusCheckResult> => {
  try {
    const response = await httpClient.get<FileProcessStatusCheckResponse>(
      FILE_PROCESS_STATUS(sessionId, fileIds)
    );
    const { data, success, message, errors } =
      response as FileProcessStatusCheckResponse;

    if (!data && success) {
      throw new Error(`No data returned for sessionId: ${sessionId}`);
    }

    if (success && data) {
      return { success, data };
    }
    return {
      success: false,
      message,
      errors,
    };
  } catch (error) {
    // Handle HTTP error responses (4xx, 5xx status codes)
    const errorResponse = error as {
      message?: string;
      errors?: string[];
    };
    Logger.error(
      new Error(errorResponse.message || 'File process status check failed')
    );

    // Return error with message for specific error handling
    return {
      success: false,
      message: errorResponse.message,
      errors: errorResponse.errors || [],
    };
  }
};

export const deleteFileRequestApiCall = async (
  sessionId: string,
  fileIds?: string[],
  fileName?: string
): Promise<FileDeleteResult> => {
  try {
    const response = await httpClient.delete<FileDeleteResponse>(
      FILE_DELETE(sessionId, fileIds, fileName)
    );
    const { data, success } = response as FileDeleteResponse;
    if (success && data) {
      return { success, data };
    }
    return {
      success: false,
    };
  } catch (error) {
    const postError = error as ApiError;
    Logger.error(new Error(postError.message));
    return {
      success: false,
    };
  }
};

export const getSessionDetailsApiCall = async (): Promise<GetSessionResult> => {
  try {
    const USER_SESSION_URL = GET_USER_SESSION_URL();
    const response = await httpClient.get<GetSessionReponse>(USER_SESSION_URL);
    const { data, success } = response as GetSessionReponse;
    if (success && data) {
      return { success, data };
    }
    return {
      success: false,
    };
  } catch (error) {
    const postError = error as ApiError;
    Logger.error(new Error(postError.message));
    return {
      success: false,
    };
  }
};

export const uploadFileApiCall = async (
  preSignedURL: string,
  file: File,
  signal: AbortSignal,
  onUploadProgress: (progressEvent: AxiosProgressEvent) => void
): Promise<UploadFileResponse> => {
  try {
    const response = await httpClient.put<UploadFileResponse>(
      preSignedURL,
      file,
      { 'Content-Type': file.type },
      {
        signal,
        onUploadProgress,
      },
      true
    );
    const { status, success } = response as UploadFileResponse;
    if (success && status === 200) {
      return { success, status };
    }
    return {
      success: false,
      status,
    };
  } catch (error) {
    const postError = error as ApiError;
    Logger.error(new Error(postError.message));
    return {
      success: false,
      status: 500,
    };
  }
};

/**
 * Fetches the file repository details for a given session.
 *
 * This function sends a GET request to retrieve the list of files associated with a session.
 * It handles the response and errors appropriately, logging any errors encountered during the process.
 *
 * @param {string} sessionId - The session ID for which to retrieve the file repository.
 * @returns {Promise<FileRepositoryResult>} - A promise that resolves to the file repository result.
 */
export const getFileRepositoryApiCall = async (
  sessionId: string
): Promise<FileRepositoryResult> => {
  try {
    const response = await httpClient.get<FileRepositoryResponse>(
      FILE_REPOSITORY(sessionId)
    );
    const { data, success } = response as FileRepositoryResponse;
    if (success && data) {
      return { success, data };
    }
    return {
      success: false,
    };
  } catch (error) {
    const getError = error as ApiError;
    Logger.error(new Error(getError.message));
    return {
      success: false,
    };
  }
};

/**
 * Updates file data in the repository.
 *
 * This function sends a PATCH request to update file information in the repository.
 * It handles the response and errors appropriately, logging any errors encountered during the process.
 *
 * @param {FileUpdatePayload} payload - The update payload containing sessionId, fileId, and fileDataUpdate.
 * @returns {Promise<FileUpdateResult>} - A promise that resolves to the file update result.
 */
export const updateFileApiCall = async (
  payload: FileUpdatePayload
): Promise<FileUpdateResult> => {
  try {
    const response = await httpClient.patch<FileUpdateResponse>(
      FILE_UPDATE,
      payload
    );
    const { data, success } = response as FileUpdateResponse;
    if (success && data) {
      return { success, data };
    }
    return {
      success: false,
    };
  } catch (error) {
    const patchError = error as ApiError;
    Logger.error(new Error(patchError.message));
    return {
      success: false,
    };
  }
};

/**
 * Initiates file download by getting presigned URL.
 *
 * This function sends a GET request to retrieve the presigned URL for downloading files.
 * It handles the response and errors appropriately, logging any errors encountered during the process.
 *
 * @param {string} sessionId - The session ID associated with the files.
 * @param {string[]} fileIds - Array of file IDs to download.
 * @returns {Promise<FileDownloadResult>} - A promise that resolves to the file download result with presigned URL.
 */
export const getFileDownloadUrlApiCall = async (
  sessionId: string,
  fileIds: string[]
): Promise<FileDownloadResult> => {
  try {
    const response = await httpClient.get<FileDownloadResponse>(
      FILE_DOWNLOAD(sessionId, fileIds)
    );
    const { data, success, message, errors } = response as FileDownloadResponse;
    if (success && data) {
      return { success, data };
    }
    return {
      success: false,
      message,
      errors,
    };
  } catch (error) {
    const getError = error as ApiError;
    Logger.error(new Error(getError.message));
    return {
      success: false,
      message: getError.message,
      errors: [getError.message],
    };
  }
};

/**
 * Downloads a file from a presigned URL.
 *
 * This function fetches the file as a blob and triggers download without opening in a new tab.
 * It creates a temporary object URL, uses an anchor element to initiate download, then cleans up.
 *
 * @param {string} presignedUrl - The presigned URL from which to download the file.
 * @param {string} fileName - The suggested name for the downloaded file.
 * @returns {Promise<{ success: boolean }>} - A promise that resolves to the download result.
 */
export const downloadFileFromPresignedUrl = async (
  presignedUrl: string,
  fileName?: string
): Promise<{ success: boolean }> => {
  try {
    // Fetch the file as a blob
    const response = await fetch(presignedUrl);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();

    // Create a temporary object URL for the blob
    const blobUrl = window.URL.createObjectURL(blob);

    // Create a temporary anchor element
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName || 'download';
    link.style.display = 'none';

    // Append to body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the object URL after a short delay
    setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl);
    }, 100);

    return { success: true };
  } catch (error) {
    const downloadError = error as Error;
    Logger.error(new Error(`File download failed: ${downloadError.message}`));
    return { success: false };
  }
};
